import { useNavigate } from "react-router-dom";
import BIDNavbar from "@/components/feature/BIDNavbar";
import BIDFooter from "@/components/feature/BIDFooter";
import HeroServiceLogo from "@/components/feature/HeroServiceLogo";
import { departments } from "@/mocks/departments";
import { useState, useCallback, useEffect, useMemo } from "react";
import { createLiveTicket, fetchLiveGroups } from "@/services/zammadService";
import { getCurrentUser } from "@/services/entraIdService";
import { useAuth } from "@/hooks/useAuth";
import AttachmentPicker from "@/components/feature/AttachmentPicker";
import { MAIN_BANNER_IMAGE } from "@/utils/images";

export default function HubPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading, login, loginWithCredentials, error: authError } = useAuth();
  const [mesaOpen, setMesaOpen] = useState(false);

  // States para login local de Zammad
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loadingCredentials, setLoadingCredentials] = useState(false);
  const [credentialsError, setCredentialsError] = useState<string | null>(null);

  const handleCredentialsLogin = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) return;

    setLoadingCredentials(true);
    setCredentialsError(null);
    try {
      await loginWithCredentials(username, password);
    } catch (err) {
      setCredentialsError(err instanceof Error ? err.message : "Error al iniciar sesión");
    } finally {
      setLoadingCredentials(false);
    }
  }, [username, password, loginWithCredentials]);

  // Zammad groups for dynamic microsites
  const [zammadGroups, setZammadGroups] = useState<{ id: number; name: string; active: boolean }[]>([]);
  const [permittedGroupIds, setPermittedGroupIds] = useState<number[] | null>(null);
  const [loadingGroups, setLoadingGroups] = useState(false);

  // Fetch groups dynamically from Zammad
  useEffect(() => {
    if (authLoading) return;

    const cacheKey = `zammad_hub_groups_v1_${user?.id || "guest"}`;
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed?.groups)) {
          setZammadGroups(parsed.groups);
          setPermittedGroupIds(parsed.permittedGroupIds || []);
        }
      } catch {
        // Ignore malformed cache; the live request below remains authoritative.
      }
    }

    let active = true;
    async function loadGroups() {
      setLoadingGroups(true);
      try {
        const res = await fetchLiveGroups(user ? { userId: user.id, email: user.email } : undefined);
        if (active) {
          if (res.error) return;
          setZammadGroups(res.groups || []);
          setPermittedGroupIds(res.permittedGroupIds || []);
          sessionStorage.setItem(cacheKey, JSON.stringify({
            groups: res.groups || [],
            permittedGroupIds: res.permittedGroupIds || [],
          }));
        }
      } catch (err) {
        console.error("Error loading groups on hub:", err);
      } finally {
        if (active) {
          setLoadingGroups(false);
        }
      }
    }
    loadGroups();
    return () => {
      active = false;
    };
  }, [user, authLoading]);

  // Slugify helper to redirect to clean slugs
  const getSlugForGroupName = (name: string) => {
    const normalized = (name || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();

    if (normalized.includes("servicio de ti") || normalized === "ti") return "ti";
    if (normalized.includes("administracion")) return "administracion";
    if (normalized.includes("servicios generales")) return "servicios-generales";
    if (normalized.includes("talento humano")) return "talento-humano";
    if (normalized.includes("legal")) return "legal";
    if (normalized.includes("contraloria")) return "contraloria";
    if (normalized.includes("diseno") || normalized.includes("marketing")) return "diseno-marketing";

    return normalized
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");
  };

  // Resolve dynamic root-level groups permitted for the user
  const permittedMicrosites = useMemo(() => {
    if (!zammadGroups || zammadGroups.length === 0) return [];
    if (!permittedGroupIds) return [];

    // Get all permitted group names
    const permittedNames = zammadGroups
      .filter((g) => permittedGroupIds.includes(g.id))
      .map((g) => g.name);

    const visibleNames = new Set<string>();
    for (const name of permittedNames) {
      const parts = name.split("::");
      let current = "";
      for (let i = 0; i < parts.length; i++) {
        current += (i > 0 ? "::" : "") + parts[i];
        visibleNames.add(current);
      }
    }

    // Filter root level groups (no "::" in Zammad name) that lead to permitted subgroups
    const rootGroups = zammadGroups.filter(
      (g) => g.active && !g.name.includes("::") && visibleNames.has(g.name)
    );

    // Map Zammad root groups to UI microsite objects
    return rootGroups.map((g) => {
      const slug = getSlugForGroupName(g.name);
      const staticInfo = departments.find((d) => d.id === slug);
      
      const children = zammadGroups
        .filter((child) => child.active && child.name.startsWith(g.name + "::") && visibleNames.has(child.name))
        .map((child) => {
          const parts = child.name.substring(g.name.length + 2).split("::");
          return parts[0];
        });
      const uniqueChildren = Array.from(new Set(children));

      return {
        id: slug,
        groupId: g.id,
        name: g.name, // EXACT NAME FROM ZAMMAD!
        description: staticInfo?.description || `Micrositio oficial de soporte y gestión para ${g.name}.`,
        icon: staticInfo?.icon || "ri-organization-chart",
        services: uniqueChildren.map((name, i) => ({
          id: `${g.id}-svc-${i}`,
          name,
          icon: "ri-question-answer-line"
        }))
      };
    });
  }, [zammadGroups, permittedGroupIds]);

  const [formData, setFormData] = useState({
    nombre: "",
    email: "",
    departamento: "",
    tipo_reporte: "",
    descripcion: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);

  useEffect(() => {
    if (mesaOpen) {
      setError(null);
      setSubmitting(false);
      setSubmitted(false);
      setAttachments([]);
      const user = getCurrentUser();
      if (user) {
        setFormData({
          nombre: user.name || "",
          email: user.email || "",
          departamento: user.department || "",
          tipo_reporte: "",
          descripcion: "",
        });
      } else {
        setFormData({
          nombre: "",
          email: "",
          departamento: "",
          tipo_reporte: "",
          descripcion: "",
        });
      }
    }
  }, [mesaOpen]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    let tipoLabel = "General";
    if (formData.tipo_reporte === "no_se_donde") tipoLabel = "No sé a qué área reportar";
    else if (formData.tipo_reporte === "falla_mesa") tipoLabel = "Mal funcionamiento de la Mesa de Servicios";
    else if (formData.tipo_reporte === "sugerencia") tipoLabel = "Sugerencia de mejora";
    else if (formData.tipo_reporte === "otro") tipoLabel = "Otro";

    try {
      await createLiveTicket({
        title: `Reporte Despachador General: ${tipoLabel}`,
        customer: formData.email,
        group: "General",
        type: "Requerimiento",
        attachments,
        body: `Nombre completo: ${formData.nombre}
Correo electrónico: ${formData.email}
Departamento / Área: ${formData.departamento || "N/A"}
Tipo de reporte: ${tipoLabel}

Descripción del reporte:
${formData.descripcion}`,
      });

      setSubmitted(true);
      setFormData({
        nombre: "",
        email: "",
        departamento: "",
        tipo_reporte: "",
        descripcion: "",
      });
      setAttachments([]);

      setTimeout(() => {
        setSubmitted(false);
        setMesaOpen(false);
      }, 2500);
    } catch (err) {
      console.error("Failed to submit ticket to Despachador General:", err);
      setError(err instanceof Error ? err.message : "No se pudo crear la solicitud.");
    } finally {
      setSubmitting(false);
    }
  };

  const navigateTo = useCallback((path: string) => {
    navigate(path);
  }, [navigate]);

  return (
    <main className="relative min-h-screen bg-background-50">
      <BIDNavbar />

      {/* Hero */}
      <section className="relative flex h-[520px] w-full items-center justify-center overflow-hidden md:h-[620px]">
        <img
          src={MAIN_BANNER_IMAGE}
          alt="BID Group — Edificio Corporativo Principal"
          className="absolute inset-0 h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
        <div className="relative z-10 w-full px-4 md:px-6 lg:px-10 text-center">
          <div className="max-w-4xl mx-auto">
            <HeroServiceLogo />
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-4 leading-tight">
              Mesa de Servicios{" "}
              <span>BID Group</span>
            </h1>
            <p className="text-white/70 text-sm md:text-base max-w-2xl mx-auto mb-8 leading-relaxed">
              Encuentra el área adecuada, registra tus solicitudes y consulta su seguimiento desde un solo lugar. Accede a soporte de TI, administración, contraloría, diseño y demás servicios internos.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="#micrositios"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById("micrositios")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="px-6 py-3 rounded-full border border-white/70 bg-white text-primary-800 text-sm font-semibold whitespace-nowrap cursor-pointer shadow-lg shadow-black/15 transition-all duration-200 hover:-translate-y-0.5 hover:border-primary-500 hover:bg-primary-500 hover:text-white hover:shadow-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary-900"
              >
                <i className="ri-grid-line mr-2 text-sm"></i>
                Encontrar un servicio
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Micrositios Grid */}
      <section id="micrositios" className="w-full py-16 md:py-20 px-4 md:px-6 lg:px-10 bg-background-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold mb-4">
              Nuestros Servicios
            </span>
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-3">
              Micrositios Departamentales
            </h2>
            <p className="text-sm text-foreground-500 max-w-xl mx-auto">
              Cada área cuenta con su propio portal especializado para gestionar solicitudes de manera eficiente
            </p>
          </div>

          <div className="flex flex-col items-center justify-center">
            {!user ? (
              <div className="w-full max-w-md bg-white rounded-2xl border border-background-200 shadow-xl p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-primary-50 flex items-center justify-center">
                  <i className="ri-shield-user-line text-primary-500 text-3xl"></i>
                </div>
                
                <h2 className="text-xl font-heading font-bold text-foreground-950 mb-1">
                  Acceso Corporativo
                </h2>
                
                <p className="text-xs text-foreground-500 mb-6 leading-relaxed">
                  Ingresa tus credenciales o inicia sesión con tu cuenta corporativa.
                </p>
                
                {(credentialsError || authError) && (
                  <div className="mb-5 p-3.5 rounded-xl bg-red-50 border border-red-100 text-xs text-red-600 text-left flex items-start gap-2.5">
                    <i className="ri-error-warning-line text-sm mt-0.5 flex-shrink-0"></i>
                    <div>
                      <p className="font-semibold">Error de autenticación</p>
                      <p className="mt-0.5 text-red-500">{credentialsError || authError}</p>
                    </div>
                  </div>
                )}

                <form onSubmit={handleCredentialsLogin} className="space-y-4 text-left">
                  <div>
                    <label className="block text-xs font-semibold text-foreground-600 mb-1.5 uppercase tracking-wider">
                      Usuario o Correo
                    </label>
                    <div className="relative">
                      <i className="ri-user-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-base"></i>
                      <input
                        type="text"
                        required
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 rounded-xl border border-background-200 bg-background-50/50 text-sm focus:outline-none focus:border-primary-500 focus:bg-white transition-all"
                        placeholder="ejemplo@bidgroup.com"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-semibold text-foreground-600 mb-1.5 uppercase tracking-wider">
                      Contraseña
                    </label>
                    <div className="relative">
                      <i className="ri-lock-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-base"></i>
                      <input
                        type="password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 rounded-xl border border-background-200 bg-background-50/50 text-sm focus:outline-none focus:border-primary-500 focus:bg-white transition-all"
                        placeholder="••••••••"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loadingCredentials}
                    className="w-full py-3.5 rounded-xl bg-primary-500 text-white text-xs font-semibold hover:bg-primary-600 transition-all cursor-pointer shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                  >
                    {loadingCredentials ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    ) : (
                      <>
                        <i className="ri-login-box-line text-base"></i>
                        Ingresar
                      </>
                    )}
                  </button>
                </form>

                <div className="relative my-6 text-center">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-background-200"></div>
                  </div>
                  <span className="relative px-3 bg-white text-xs text-foreground-400 uppercase tracking-wider font-medium">
                    o continuar con
                  </span>
                </div>
                
                <button
                  onClick={login}
                  className="w-full inline-flex items-center justify-center gap-3 px-6 py-3.5 rounded-xl border border-background-200 bg-white text-foreground-800 text-xs font-semibold hover:bg-background-50 transition-all cursor-pointer shadow-sm hover:shadow active:scale-[0.98]"
                >
                  <i className="ri-microsoft-fill text-lg text-primary-500"></i>
                  Iniciar sesión con Microsoft
                </button>
                
                <p className="text-[11px] text-foreground-400 mt-6 leading-relaxed">
                  Mesa de Ayuda BID Group protege tus datos mediante encriptación y conexiones seguras.
                </p>
              </div>
            ) : loadingGroups ? (
              <div className="flex flex-col items-center justify-center py-10">
                <div className="w-8 h-8 border-3 border-primary-500/20 border-t-primary-500 rounded-full animate-spin mb-3"></div>
                <p className="text-xs text-foreground-500">Cargando datos...</p>
              </div>
            ) : permittedMicrosites.length === 0 ? (
              <div className="w-full max-w-md bg-white rounded-2xl border border-background-200 p-8 text-center">
                <i className="ri-shield-user-line text-foreground-400 text-4xl mb-3 block"></i>
                <h3 className="text-sm font-semibold text-foreground-800 mb-1">Sin accesos autorizados</h3>
                <p className="text-xs text-foreground-500">
                  Tu usuario no cuenta con permisos para acceder a estos servicios. Por favor contacta al administrador.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 w-full">
                {permittedMicrosites.map((dept, idx) => {
                  const accentStyles = [
                    "bg-accent-100 text-accent-600 group-hover:bg-accent-200",
                    "bg-primary-100 text-primary-600 group-hover:bg-primary-200",
                    "bg-secondary-100 text-secondary-600 group-hover:bg-secondary-200",
                  ];
                  return (
                    <a
                      key={dept.id}
                      href={`/${dept.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        navigateTo(`/${dept.id}`);
                      }}
                      className="group rounded-2xl bg-background-50 border border-background-200/80 p-5 md:p-6 cursor-pointer hover:border-primary-200 hover:bg-primary-50/30 transition-all duration-300"
                    >
                      <div className="flex items-start gap-4">
                        <div className={`w-11 h-11 md:w-12 md:h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors duration-300 ${accentStyles[idx % 3]}`}>
                          <i className={`${dept.icon} text-lg md:text-xl`}></i>
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="text-sm md:text-base font-heading font-semibold text-foreground-900 mb-1 group-hover:text-primary-700 transition-colors truncate">
                            {dept.name}
                          </h3>
                          <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2">
                            {dept.description}
                          </p>
                          <div className="flex flex-wrap gap-1.5 mt-3">
                            {dept.services.slice(0, 3).map((s) => (
                              <span
                                key={s.id}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-background-100 text-xs text-foreground-500"
                              >
                                <i className={`${s.icon} text-[10px]`}></i>
                                {s.name}
                              </span>
                            ))}
                            {dept.services.length > 3 && (
                              <span className="text-xs text-foreground-400">+{dept.services.length - 3} más</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="w-full py-16 md:py-20 px-4 md:px-6 lg:px-10 bg-background-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
            ¿Necesitas ayuda para elegir?
          </h2>
          <p className="text-sm text-foreground-500 max-w-lg mx-auto mb-8">
            Si no estás seguro de qué micrositio corresponde a tu solicitud, abre la Mesa de Servicio y el Despachador General la canalizará al área correcta.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            {/* <button
              onClick={() => setMesaOpen(true)}
              className="px-6 py-3 rounded-full bg-primary-500 text-white text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-all"
            >
              <i className="ri-customer-service-2-line mr-2 text-sm"></i>
              Mesa de Servicio
            </button>*/}
            <a
              href="#micrositios"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("micrositios")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="px-6 py-3 rounded-full bg-background-100 border border-background-200 text-foreground-700 text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-background-200 transition-all"
            >
              <i className="ri-grid-line mr-2 text-sm"></i>
              Ver Todos los Micrositios
            </a>
          </div>
        </div>
      </section>

      <BIDFooter />

      {/* Mesa de Servicio Modal */}
      {mesaOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setMesaOpen(false)}></div>
          <div className="relative z-10 w-full max-w-xl bg-background-50 rounded-2xl border border-background-200 shadow-xl p-6 md:p-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-heading font-bold text-foreground-950">Mesa de Servicio</h3>
                <p className="text-xs text-foreground-500 mt-0.5">Reporta incidencias al Despachador General</p>
              </div>
              <button
                onClick={() => setMesaOpen(false)}
                className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
              >
                <i className="ri-close-line text-foreground-600"></i>
              </button>
            </div>

            <p className="text-xs text-foreground-500 mb-5 leading-relaxed">
              Utiliza este canal para reportar mal funcionamiento de la Mesa de Servicios, o cuando no sepas a qué micrositio dirigir tu solicitud. El Despachador General la canalizará al área correspondiente.
            </p>

            {submitted ? (
              <div className="px-6 py-12 text-center animate-[fadeIn_0.2s_ease-out]">
                <div className="w-16 h-16 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-accent-600 text-2xl"></i>
                </div>
                <h4 className="text-lg font-heading font-semibold text-foreground-950 mb-2">
                  Solicitud enviada exitosamente
                </h4>
                <p className="text-sm text-foreground-600">
                  Tu reporte ha sido enviado al Despachador General. Te contactaremos pronto.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-readdy-form="">
                {error && (
                  <div className="mb-4 p-4 rounded-xl bg-red-50 border border-red-200 text-xs text-red-700 flex items-start gap-2.5 animate-[fadeIn_0.2s_ease-out]">
                    <i className="ri-error-warning-line text-base text-red-500 flex-shrink-0"></i>
                    <div className="flex-1 min-w-0">
                      <span className="font-semibold block">Error al crear la solicitud</span>
                      <span className="block mt-1 break-words">{error}</span>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-xs font-medium text-foreground-700 mb-1.5">Nombre completo *</label>
                    <input
                      type="text"
                      name="nombre"
                      value={formData.nombre}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 transition-all"
                      placeholder="Tu nombre"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground-700 mb-1.5">Correo electrónico *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      readOnly
                      title="El ticket se asociará al usuario autenticado"
                      required
                      className="w-full cursor-not-allowed rounded-lg border border-background-300 bg-background-100 px-4 py-2.5 text-sm text-foreground-600 placeholder:text-foreground-400 focus:outline-none"
                      placeholder="tu@bidgroup.com"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground-700 mb-1.5">Departamento / Área</label>
                    <input
                      type="text"
                      name="departamento"
                      value={formData.departamento}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 transition-all"
                      placeholder="Tu área de trabajo"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground-700 mb-1.5">Tipo de reporte *</label>
                    <select
                      name="tipo_reporte"
                      value={formData.tipo_reporte}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-300 transition-all cursor-pointer"
                    >
                      <option value="">Seleccionar...</option>
                      <option value="no_se_donde">No sé a qué área reportar</option>
                      <option value="falla_mesa">Mal funcionamiento de la Mesa de Servicios</option>
                      <option value="sugerencia">Sugerencia de mejora</option>
                      <option value="otro">Otro</option>
                    </select>
                  </div>
                </div>
                <div className="mb-5">
                  <label className="block text-xs font-medium text-foreground-700 mb-1.5">Descripción del reporte *</label>
                  <textarea
                    name="descripcion"
                    value={formData.descripcion}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 transition-all resize-none"
                    placeholder="Describe tu situación con detalle para que el Despachador pueda canalizarla correctamente"
                  ></textarea>
                </div>
                <div className="mb-5">
                  <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                    Archivos adjuntos
                  </label>
                  <AttachmentPicker
                    files={attachments}
                    onChange={setAttachments}
                    disabled={submitting}
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full px-6 py-3 rounded-full bg-primary-500 text-white text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-all flex items-center justify-center gap-2 disabled:bg-primary-300 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                  ) : (
                    <i className="ri-send-plane-line mr-2 text-sm"></i>
                  )}
                  {submitting ? "Enviar al Despachador..." : "Enviar al Despachador"}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </main>
  );
}
